import { IconButton, Stack } from '@mui/material';
import React, { useContext, useState } from 'react';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import { CustomCellProps } from 'components/common/grid/';
import IconMi from 'components/common/icon/IconMi';
import { MetricChartCompareLineType } from 'components/metric/chart';
import { prepareFormComponentValues } from 'components/common/form/layout/control';
import { RawFormComponentType, FormDataAPIType } from 'components/common/form';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import {
    MetricOverlaysContext,
    MetricOverlaysContextType,
} from 'components/metric/collaboration-controls/overlay-metrics/OverlayMetrics';
import { prepareFormElementProps } from 'components/common/form/formTools';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { FormElementWrapper } from 'components/common/form/element/FormElementWrapper';
import useBundleTranslation from 'i18n';
import useGridDeleteConfirmation from 'components/common/grid/buttons/useGridDeleteConfirmation';
import { ChartLineTypes } from 'components/chart';

function FormDashSelect({ elementProps }: { elementProps: FormDataAPIType }) {
    const selectData = prepareFormComponentValues(ChartLineTypes);

    const selectProps = prepareFormElementProps({
        ...elementProps,
        ...{
            label: 'widthLabel',
            component: {
                component: 'FormSelect',
                name: 'dashStyle',
                label: 'Line type',
            } as RawFormComponentType,
        },
    });

    return (
        <FormElementWrapper elementProps={selectProps}>
            <ReactHookFormController componentValues={selectData} elementProps={selectProps} />
        </FormElementWrapper>
    );
}

function FormColorPicker({ elementProps }: { elementProps: FormDataAPIType }) {
    const colorPickerProps = prepareFormElementProps({
        ...elementProps,
        ...{
            label: 'widthLabel',
            component: {
                component: 'ColorPicker',
                name: 'color',
                label: 'Color',
            } as RawFormComponentType,
        },
    });

    return (
        <FormElementWrapper elementProps={colorPickerProps}>
            <ReactHookFormController elementProps={colorPickerProps} />
        </FormElementWrapper>
    );
}

export default function CompareLinesGridDataCell({ reloadGridData, data }: CustomCellProps) {
    const { t } = useBundleTranslation(['main', 'components/common/grid/grid', 'components/common/form/form']);
    const context = useContext<MetricOverlaysContextType | null>(MetricOverlaysContext);

    const lineData: MetricChartCompareLineType = {
        ...data,
        //@ts-ignore
        id: data.compare_line_id,
        //@ts-ignore
        dashStyle: data.dash_style_raw,
    } as unknown as MetricChartCompareLineType;

    const elementProps = useCustomSimplifiedForm({ dashStyle: lineData.dashStyle, color: lineData.color });

    const onDeleteClick = () => {
        setOpenConfirmation(true);
    };

    const onDeleteConfirmed = () => {
        setOpenConfirmation(false);
        if (!context?.onDelete) {
            return;
        }
        context.onDelete('compareLinesGrid', lineData.id);
    };
    const { ConfirmationPopup, setOpenConfirmation } = useGridDeleteConfirmation(
        t,
        {},
        onDeleteConfirmed,
        true,
        'compare_line',
    );

    const popupSettings: PopupSettings = {
        title: 'overlay_options',
    };

    const onEditClick = () => {
        setIsEdit(true);
    };

    const handleSaveForm = () => {
        if (!context?.onUpdate) {
            return;
        }
        const values = elementProps.form.hookFormGetValues();
        context?.onUpdate('compareLinesGrid', {
            id: lineData.id,
            color: values.color,
            dashStyle: values.dashStyle,
        });
        setIsEdit(false);
    };

    const [isEdit, setIsEdit] = useState<boolean>(false);

    return (
        <Stack direction={'row'} sx={{ height: 1, alignItems: 'center', justifyContent: 'end' }}>
            {isEdit && (
                <Popup
                    settings={popupSettings}
                    open={isEdit}
                    onHide={() => setIsEdit(false)}
                    onConfirm={() => handleSaveForm()}
                >
                    <Stack spacing={2}>
                        <FormDashSelect elementProps={elementProps} />
                        <FormColorPicker elementProps={elementProps} />
                    </Stack>
                </Popup>
            )}
            <IconButton aria-label="edit" size={'small'} onClick={onEditClick}>
                <IconMi icon="gear" fontSize={'16'} />
            </IconButton>
            <IconButton aria-label="delete" size={'small'} onClick={onDeleteClick}>
                <IconMi icon="delete" fontSize={'16'} />
            </IconButton>
            <ConfirmationPopup />
        </Stack>
    );
}
